<template>
    <div class="blog-page">
        <!-- Hero Section -->
        <Section class="blog-hero-section">
            <div class="blog-hero-container">
                <h1>{{ t('blog.hero.title') }}</h1>
                <p class="blog-subtitle">
                    {{ t('blog.hero.subtitle') }}
                </p>
            </div>
        </Section>

        <!-- Blog Posts Grid -->
        <Section class="blog-posts-section">
            <div class="page-container">
                <v-row>
                    <v-col
                        v-for="post in blogPosts"
                        :key="post.slug"
                        cols="12"
                        md="6"
                        lg="4"
                    >
                        <v-card
                            class="blog-post-card"
                            elevation="0"
                            :to="getLocalizedPath(`/blog/${post.slug}`)"
                        >
                            <v-card-text>
                                <div class="post-meta">
                                    <span class="post-date">{{ post.date }}</span>
                                    <span class="post-reading-time"
                                        >{{ post.readingTime }} {{ t('blog.reading_time') }}</span
                                    >
                                </div>
                                <h2 class="post-title">{{ post.title }}</h2>
                                <p class="post-excerpt">{{ post.excerpt }}</p>
                                <div class="post-cta">
                                    <span class="read-more">Read more</span>
                                    <SmartIcon
                                        icon="arrow-right"
                                        size="sm"
                                        color="primary"
                                    />
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </Section>

        <DemoCredentialsDialog
            :model-value="showDemoDialog"
            :title="t('cta.demo_overlay.title')"
            :hint="t('cta.demo_overlay.hint')"
            :username-label="t('cta.demo_overlay.username')"
            :password-label="t('cta.demo_overlay.password')"
            :cancel-label="t('cta.demo_overlay.cancel')"
            :open-demo-label="t('cta.demo_overlay.open')"
            @update:model-value="showDemoDialog = $event"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, onUnmounted } from 'vue'
    import { useRoute } from 'vue-router'
    import Section from '@/components/Section.vue'
    import SmartIcon from '@/components/common/SmartIcon.vue'
    import DemoCredentialsDialog from '@/components/DemoCredentialsDialog.vue'
    import { useSEO } from '@/composables/useSEO'
    import { useTranslations } from '@/composables/useTranslations'

    const { t, get, currentLanguage } = useTranslations()
    const route = useRoute()
    const showDemoDialog = ref(false)

    const getLocalizedPath = (path: string) => {
        const lang = (route.params.lang as string) || currentLanguage.value
        return `/${lang}${path === '/' ? '' : path}`
    }

    // Define available blog posts
    const blogPostSlugs = [
        'email-notifications',
        'headless-authentication',
        'entities-and-definitions',
        'entity-hierarchies',
    ]

    const blogPosts = computed(() => {
        return blogPostSlugs.map(slug => {
            const post = get<Record<string, string>>(`blog.posts.${slug}`)
            return {
                slug,
                title: post?.title ?? slug,
                excerpt: post?.excerpt ?? '',
                date: post?.date ?? '',
                readingTime: post?.reading_time ?? '5',
            }
        })
    })

    // Listen for the open-demo event from Header
    const handleOpenDemo = () => {
        showDemoDialog.value = true
    }

    onMounted(() => {
        window.addEventListener('open-demo', handleOpenDemo)
    })

    onUnmounted(() => {
        window.removeEventListener('open-demo', handleOpenDemo)
    })

    useSEO({
        title: () => t('blog.seo.title'),
        description: () => t('blog.seo.description'),
    })
</script>

<style scoped>
    .blog-page {
        padding-top: 80px;
    }

    .page-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 16px;
    }

    /* Hero Section */
    .blog-hero-section {
        padding: 80px 0 60px;
        text-align: center;
        background: linear-gradient(
            180deg,
            rgba(var(--v-theme-surface), 1) 0%,
            rgba(var(--v-theme-surface-variant), 0.3) 100%
        );
    }

    .blog-hero-container {
        max-width: 800px;
        margin: 0 auto;
    }

    .blog-hero-section h1 {
        font-size: clamp(2.5rem, 5vw, 4rem);
        font-weight: 800;
        line-height: 1.1;
        margin: 0 0 24px;
        color: rgb(var(--v-theme-on-surface));
    }

    .blog-subtitle {
        font-size: 1.25rem;
        line-height: 1.6;
        color: rgb(var(--v-theme-on-surface-variant));
        margin: 0;
    }

    /* Blog Posts Section */
    .blog-posts-section {
        padding: 60px 0 80px;
    }

    .blog-post-card {
        height: 100%;
        border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
        transition: all 0.3s ease;
        cursor: pointer;
        text-decoration: none;
    }

    .blog-post-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        border-color: rgb(var(--v-theme-primary));
    }

    .post-meta {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;
        font-size: 0.875rem;
        color: rgb(var(--v-theme-on-surface-variant));
    }

    .post-date {
        font-weight: 500;
    }

    .post-reading-time {
        opacity: 0.8;
    }

    .post-title {
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1.3;
        margin: 0 0 12px;
        color: rgb(var(--v-theme-on-surface));
    }

    .post-excerpt {
        font-size: 1rem;
        line-height: 1.6;
        color: rgb(var(--v-theme-on-surface-variant));
        margin: 0 0 20px;
    }

    .post-cta {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgb(var(--v-theme-primary));
        font-weight: 600;
    }

    @media (max-width: 960px) {
        .blog-page {
            padding-top: 60px;
        }

        .blog-hero-section {
            padding: 60px 0 40px;
        }

        .blog-posts-section {
            padding: 40px 0 60px;
        }
    }
</style>
